import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface RowAction {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
}

interface RowActionsMenuProps {
  /** Accessible name for the trigger, e.g. "More actions for <event title>". */
  label: string;
  items: RowAction[];
}

const ITEM_HEIGHT = 36;

/**
 * "⋯" overflow menu for a table row. The menu is portalled to <body> and
 * position: fixed against the trigger, so neither the table's overflow-x
 * container nor the sticky actions cells of later rows (each its own stacking
 * context) can clip or cover it — and it opens upwards when the row is near the
 * bottom of the viewport. Any scroll or resize closes it instead of leaving it
 * floating away from its row.
 */
export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ label, items }) => {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    menuRef.current?.querySelector('button')?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  if (items.length === 0) return null;

  const toggle = () => setAnchor(anchor ? null : buttonRef.current?.getBoundingClientRect() ?? null);

  const menuHeight = items.length * ITEM_HEIGHT + 8;
  const openUp = anchor ? anchor.bottom + menuHeight + 8 > window.innerHeight : false;
  const style: React.CSSProperties | undefined = anchor
    ? {
        right: window.innerWidth - anchor.right,
        ...(openUp ? { bottom: window.innerHeight - anchor.top + 4 } : { top: anchor.bottom + 4 }),
      }
    : undefined;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      >
        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </button>
      {anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={style}
          className="fixed z-40 min-w-[11rem] rounded-lg border border-gray-200 bg-white py-1 text-left shadow-lg"
        >
          {items.map(item => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setAnchor(null);
                item.onSelect();
              }}
              className={`block w-full px-4 py-2 text-left text-sm focus:outline-none ${
                item.destructive
                  ? 'text-red-700 hover:bg-red-50 focus:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};
